import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import TIcon from "../../components/ui/TIcon";
import * as SBService from "../../helpers/sportbookService";
import { getAssetFullUrl, toCurrency } from "../../helpers/utils";
import {
  bottomBarState,
  loaderState,
  showBottomBarState,
  userState,
} from "../../states/common";

const BOARDS = [
  { value: "biadi", label: "ĐIỂM BIADI" },
  { value: "fargo", label: "FARGORATE" },
  { value: "wpa", label: "WPA 9-BALL" },
];

/**
 * Hai bảng ngoài: dữ liệu do backend chụp lại từ trang nguồn rồi phục vụ bản
 * chụp, front office không bao giờ gọi thẳng sang đó.
 *
 * Hạng mục con chia đúng như trang nguồn chia, không gộp lại: "Nam/Nữ" của WPA
 * và "Thế giới/Nữ" của FargoRate là bốn bảng khác nhau, không phải một bảng lọc
 * theo giới tính.
 */
const NGUON_NGOAI: Record<
  string,
  {
    nguon: string;
    hangMuc: Array<{ value: string; label: string }>;
    tai: (board: string) => Promise<any>;
  }
> = {
  fargo: {
    nguon: "FargoRate (fargorate.com)",
    hangMuc: [
      { value: "world", label: "Thế giới" },
      { value: "world-female", label: "Nữ" },
    ],
    tai: (board) => SBService.getFargoRankings(board as "world" | "world-female"),
  },
  wpa: {
    nguon: "WPA (wpapool.com)",
    hangMuc: [
      { value: "men", label: "Nam" },
      { value: "women", label: "Nữ" },
    ],
    tai: (board) => SBService.getWpaRankings(board as "men" | "women"),
  },
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/** Màn 04 — Bảng xếp hạng.
 *
 *  Ba bảng, hai loại nguồn khác hẳn nhau: "Điểm Biadi" là cơ thủ trong hệ thống
 *  (/players sắp theo điểm), còn "FargoRate" và "WPA 9-Ball" là bảng thế giới do
 *  backend chụp lại từ trang nguồn — nên hai bảng ngoài hiển thị cả thời điểm
 *  chụp, và không có khái niệm "hạng của tôi". */
const RankingsPage = () => {
  const [, setLoader] = useRecoilState(loaderState);
  const [, setActive] = useRecoilState(bottomBarState);
  const [, setBottomBar] = useRecoilState(showBottomBarState);
  const [user] = useRecoilState(userState);

  const [board, setBoard] = useState("biadi");
  const [players, setPlayers] = useState<any[]>([]);

  /* Hạng mục đang chọn của TỪNG bảng ngoài. Nhớ riêng cho mỗi bảng: đang xem
     "Nữ" của FargoRate rồi nhảy sang WPA và quay lại thì vẫn là "Nữ", chứ không
     bị kéo về mặc định. */
  const [hangMuc, setHangMuc] = useState<Record<string, string>>({});

  /* Giữ mọi hạng mục đã tải, khoá theo "bảng:hạng mục". Đổi qua lại là chuyện
     thường, tải lại mỗi lần đổi thì lần nào cũng chớp loader dù dữ liệu đã có
     sẵn trong máy. */
  const [banChup, setBanChup] = useState<
    Record<string, { rows: any[]; fetchedAt: string | null }>
  >({});

  const nguon = NGUON_NGOAI[board];
  const hangMucDangXem = nguon ? hangMuc[board] ?? nguon.hangMuc[0].value : null;
  const khoa = nguon ? `${board}:${hangMucDangXem}` : null;

  useEffect(() => {
    document.title = "Biadi — Bảng xếp hạng";
    setActive("ranking");
    setBottomBar(true);

    const load = async () => {
      setLoader(true);
      const rsp = await SBService.getTopPlayers(50).catch(() => null);
      setPlayers(rsp?.data ?? []);
      setLoader(false);
    };
    load();
  }, []);

  const banChupDangXem = khoa ? banChup[khoa] : undefined;

  /* Bảng ngoài chỉ tải khi người xem mở tới, và mỗi hạng mục tải đúng một lần. */
  useEffect(() => {
    if (!nguon || !khoa || !hangMucDangXem || banChup[khoa]) return;
    let cancelled = false;
    const load = async () => {
      setLoader(true);
      const rsp = await nguon.tai(hangMucDangXem).catch(() => null);
      if (!cancelled) {
        /* Backend bọc response trong { data } khi payload không có đủ khoá phân
           trang, nên tuỳ phiên bản đang chạy mà mảng nằm ở rsp.data hoặc
           rsp.data.data. Nhận cả hai để bản FE này không phụ thuộc thứ tự
           deploy, và không bao giờ đưa thứ khác mảng vào state. */
        const body = Array.isArray(rsp?.data) ? rsp : rsp?.data;
        const rows = Array.isArray(body?.data) ? body.data : [];
        // Chỉ nhớ kết quả có dữ liệu. Nhớ cả kết quả rỗng thì một lần lỗi mạng
        // sẽ dính luôn tới khi tải lại trang, vì lượt sau bị coi là "đã tải".
        if (rows.length > 0) {
          setBanChup((all) => ({
            ...all,
            [khoa]: { rows, fetchedAt: body?.fetchedAt ?? null },
          }));
        }
        setLoader(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [nguon, khoa, hangMucDangXem, banChup]);

  /* Hai nguồn khác nhau nhưng cùng một hình dạng hàng, để phần hiển thị bên
     dưới không phải rẽ nhánh theo bảng. */
  const rows = useMemo(
    () =>
      nguon
        ? (banChupDangXem?.rows ?? []).map((row: any) => ({
            key: `${row.position}-${row.name}`,
            name: row.name,
            score: row.points,
            note: row.country ?? null,
            picture: null,
          }))
        : players.map((p: any) => ({
            key: p.id,
            name: p.name,
            score: p.score ?? 0,
            note: null,
            picture: p.profilePicture ?? null,
          })),
    [nguon, players, banChupDangXem]
  );

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  const myRow = useMemo(() => {
    if (nguon) return null;
    const idx = players.findIndex((p) => p.id === user.id);
    return idx >= 0 ? { player: players[idx], position: idx + 1 } : null;
  }, [nguon, players, user.id]);

  /* Thứ tự trên bục: hạng 2 — hạng 1 — hạng 3 */
  const podiumOrder = [
    { p: podium[1], pos: 2 },
    { p: podium[0], pos: 1 },
    { p: podium[2], pos: 3 },
  ];

  return (
    <>
      <header className="page-head">
        <h1>BẢNG XẾP HẠNG</h1>
      </header>

      <div className="seg">
        {BOARDS.map((b) => (
          <button
            key={b.value}
            type="button"
            className={`seg-item${board === b.value ? " active" : ""}`}
            onClick={() => setBoard(b.value)}
          >
            {b.label}
          </button>
        ))}
      </div>

      {nguon ? (
        <div className="chips" style={{ paddingTop: 14 }}>
          {nguon.hangMuc.map((b) => (
            <button
              key={b.value}
              type="button"
              className={`chip${hangMucDangXem === b.value ? " active" : ""}`}
              onClick={() => setHangMuc((all) => ({ ...all, [board]: b.value }))}
            >
              {b.label}
            </button>
          ))}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="ui-card empty-state" style={{ marginTop: 22 }}>
          <TIcon name="list-numbers" />
          <div className="t">Chưa có dữ liệu xếp hạng</div>
          <div className="h">
            {nguon
              ? `Chưa lấy được bảng xếp hạng từ ${nguon.nguon}, thử lại sau`
              : "Bảng xếp hạng sẽ hiện khi có cơ thủ được thẩm định"}
          </div>
        </div>
      ) : (
        <>
          {/* ── Bục 3 hạng đầu ── */}
          <div className="podium">
            {podiumOrder.map(({ p, pos }) =>
              p ? (
                <div
                  key={p.key}
                  className={`podium-card${pos === 1 ? " first" : ""}`}
                >
                  {pos === 1 ? <span className="crown">HẠNG 1</span> : null}
                  <span
                    className={`avatar${pos === 1 ? " avatar-ring" : ""}`}
                    style={
                      pos === 1
                        ? { width: 54, height: 54, fontSize: 15 }
                        : { width: 42, height: 42, fontSize: 12 }
                    }
                  >
                    {p.picture ? (
                      <img src={getAssetFullUrl(p.picture)} alt="" />
                    ) : (
                      initials(p.name)
                    )}
                  </span>
                  <span className="name">{p.name}</span>
                  <span className="pts">
                    {p.score === null ? "—" : toCurrency(p.score)}
                    {nguon ? "" : " đ"}
                  </span>
                  <span className="num">{pos}</span>
                </div>
              ) : (
                <div key={pos} className="podium-card" style={{ opacity: 0 }} />
              )
            )}
          </div>

          {/* ── Bảng từ hạng 4 ── */}
          <div className="ui-card list" style={{ marginTop: 18 }}>
            <div className="table-head">
              <span style={{ width: 26 }}>#</span>
              <span style={{ flex: 1 }}>CƠ THỦ</span>
              <span>ĐIỂM</span>
            </div>

            {rest.map((p, i) => (
              <div key={p.key ?? i} className="list-row rank-row">
                <span className="pos">{i + 4}</span>
                <span className="rank-name">
                  {p.name}
                  {p.note ? <span className="rank-note">{p.note}</span> : null}
                </span>
                <span className="rank-pts">
                  {p.score === null ? "—" : toCurrency(p.score)}
                </span>
              </div>
            ))}
          </div>

          {myRow ? (
            <div className="you-card">
              <span className="pos">{myRow.position}</span>
              <span className="name">Bạn — {myRow.player.name}</span>
              <span className="rank-pts">
                {toCurrency(myRow.player.score ?? 0)}
              </span>
            </div>
          ) : null}

          <p className="caption">
            {nguon ? (
              <>
                Nguồn: {nguon.nguon} · Cập nhật{" "}
                {banChupDangXem?.fetchedAt
                  ? moment(banChupDangXem.fetchedAt).format("DD/MM/YYYY HH:mm")
                  : "—"}
              </>
            ) : (
              <>
                Cập nhật {moment().format("DD/MM/YYYY")} · Xếp theo điểm hạng cơ
                Biadi
              </>
            )}
          </p>
        </>
      )}
    </>
  );
};

export default RankingsPage;
